import type {
  AdminOverview,
  AdminTaskDiagnostics,
  ApiKey,
  AuditEntry,
  ImageTaskInput,
  MediaCatalog,
  ModelConfig,
  PageResult,
  PricingDraftInput,
  AggAPIVideoPriceUpdateInput,
  AggAPIVideoPriceVersion,
  PriceQuote,
  PriceVersion,
  Provider,
	AlertWebhook,
	ModelRoute,
	ProviderAlert,
	VideoPointPrice,
  VideoRouteFilter,
  Task,
  TaskEvent,
  User,
  VideoTaskInput
} from "./types";

type VideoPointPriceUpdate =
  | { billing_unit: "second"; points_per_second: string }
  | { billing_unit: "request"; points_per_request: string };

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

interface ErrorBody {
  code?: string;
  message?: string;
}

interface Envelope<T> {
  data: T;
  error?: ErrorBody | null;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  headers.set("Accept", "application/json");
  const method = (init.method ?? "GET").toUpperCase();
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    const token = csrfToken();
    if (token) headers.set("X-CSRF-Token", token);
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    credentials: "include"
  });
  const isJSON = response.headers.get("content-type")?.includes("application/json");
  const body = isJSON ? ((await response.json()) as Envelope<T> | T) : undefined;

  if (!response.ok) {
    if (response.status === 401 && !["/api/v1/auth/login", "/api/v1/auth/register"].includes(path)) window.dispatchEvent(new Event("media:unauthorized"));
    const envelope = body as Envelope<T> | undefined;
    const detail = envelope?.error;
    throw new ApiError(detail?.message ?? `请求失败 (${response.status})`, response.status, detail?.code);
  }

  if (body && typeof body === "object" && "data" in body) {
    const envelope = body as Envelope<T>;
    if (envelope.error) {
      throw new ApiError(envelope.error.message ?? "请求失败", response.status, envelope.error.code);
    }
    return envelope.data;
  }

  return body as T;
}

function queryString(params: Record<string, string | number | boolean | undefined>) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") search.set(key, String(value));
  }
  const value = search.toString();
  return value ? `?${value}` : "";
}

function normalizePage<T>(value: PageResult<T> | T[]): PageResult<T> {
  if (Array.isArray(value)) return { items: value, total: value.length, page: 1, page_size: value.length };
  return value;
}

function normalizeUser(value: User): User {
  const legacy = value as User & { email?: string; points?: Partial<User["points"]> };
  return {
    ...value,
    username: value.username ?? legacy.email ?? "",
    name: value.name ?? value.display_name,
    points: {
      balance: legacy.points?.balance ?? "0.000000",
      reserved: legacy.points?.reserved ?? "0.000000",
      available: legacy.points?.available ?? "0.000000"
    }
  };
}

function normalizeTask(value: Task): Task {
  const legacy = value as Task & { error_message?: string; error_code?: string; assets?: Task["data"] };
  return {
    ...value,
    status: value.status ?? value.internal_status ?? "queued",
    data: value.data ?? legacy.assets ?? [],
    error: value.error ?? (legacy.error_message ? { code: legacy.error_code, message: legacy.error_message } : null)
  };
}

function normalizeApiKey(value: ApiKey): ApiKey {
  return { ...value, prefix: value.prefix ?? value.key_prefix ?? "" };
}

function csrfToken() {
  for (const name of ["media_csrf", "csrf_token", "csrf"]) {
    const match = document.cookie.split("; ").find((entry) => entry.startsWith(`${name}=`));
    if (match) return decodeURIComponent(match.slice(name.length + 1));
  }
  return "";
}

export const api = {
  register: async (input: { username: string; password: string }) =>
    normalizeUser(await request<User>("/api/v1/auth/register", { method: "POST", body: JSON.stringify(input) })),
  login: async (input: { username: string; password: string }) =>
    normalizeUser(await request<User>("/api/v1/auth/login", { method: "POST", body: JSON.stringify(input) })),
  logout: () => request<void>("/api/v1/auth/logout", { method: "POST" }),
  me: async () => normalizeUser(await request<User>("/api/v1/auth/me")),
  catalog: () => request<MediaCatalog>("/api/v1/catalog"),

  listTasks: async (params: { page?: number; page_size?: number; status?: string; modality?: string; query?: string }) => {
    const serverParams = params;
    const page = normalizePage(await request<PageResult<Task> | Task[]>(`/api/v1/tasks${queryString(serverParams)}`));
    const query = params.query?.trim().toLowerCase();
    const items = page.items.map(normalizeTask).filter((task) => (params.status !== "active" || ["preparing", "submitted", "in_progress", "storing"].includes(task.status)) && (!query || task.id.toLowerCase().includes(query) || task.model.toLowerCase().includes(query)));
    return { ...page, items, total: params.status === "active" || query ? items.length : page.total };
  },
  getTask: async (id: string) => normalizeTask(await request<Task>(`/api/v1/tasks/${encodeURIComponent(id)}`)),
  listTaskEvents: (id: string) => request<TaskEvent[]>(`/api/v1/tasks/${encodeURIComponent(id)}/events`),
  createImageTask: async (input: ImageTaskInput, idempotencyKey: string) =>
    normalizeTask(await request<Task>("/api/v1/tasks/images", {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify(input)
    })),
  createVideoTask: async (input: VideoTaskInput, idempotencyKey: string) =>
    normalizeTask(await request<Task>("/api/v1/tasks/videos", {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify(input)
    })),
  quote: (params: { model: string; modality: "image" | "video"; mode: string; resolution: string; quality?: string; n?: number; duration_seconds?: number; ratio?: string; image_reference_count?: number; audio_reference_count?: number; video_reference_count?: number; protect_stripe?: boolean; prompt_length?: number }) =>
    request<PriceQuote>(`/api/v1/pricing/quote${queryString(params)}`),

  listApiKeys: async () => (await request<ApiKey[]>("/api/v1/api-keys")).map(normalizeApiKey),
  createApiKey: async (input: { name: string; scopes?: string[] }) => {
    type CreationResult = ApiKey & { api_key?: ApiKey; key?: string | ApiKey; token?: string };
    const result = await request<CreationResult>("/api/v1/api-keys", { method: "POST", body: JSON.stringify(input) });
    const record = result.api_key ?? (typeof result.key === "object" ? result.key : result);
    const secret = typeof result.key === "string" ? result.key : result.token ?? "";
    return { ...normalizeApiKey(record), key: secret };
  },
  revokeApiKey: (id: string) => request<void>(`/api/v1/api-keys/${encodeURIComponent(id)}`, { method: "DELETE" }),

  getAdminOverview: () => request<AdminOverview>("/api/v1/admin/overview"),
  listUsers: async (params: { page?: number; query?: string; status?: string }) => {
    const page = normalizePage(await request<PageResult<User> | User[]>(`/api/v1/admin/users${queryString(params)}`));
    const query = params.query?.trim().toLowerCase();
    const items = page.items.map(normalizeUser).filter((user) => (!params.status || user.status === params.status) && (!query || user.username.toLowerCase().includes(query) || user.name?.toLowerCase().includes(query)));
    return { ...page, items, total: params.status || query ? items.length : page.total };
  },
  createUser: async (input: { username: string; display_name: string; password: string; role: string }) =>
    normalizeUser(await request<User>("/api/v1/admin/users", { method: "POST", body: JSON.stringify(input) })),
  updateUser: (id: string, input: Partial<Pick<User, "status" | "role">>) =>
    request<User>(`/api/v1/admin/users/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify(input) }),
  adjustUserPoints: (id: string, input: { delta: string; reason: string }, idempotencyKey: string) =>
    request<unknown>(`/api/v1/admin/users/${encodeURIComponent(id)}/point-adjustments`, {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify(input)
    }),
  listProviders: async () => (await request<Provider[]>("/api/v1/admin/providers")).map((value) => ({ ...value, last_checked_at: value.last_checked_at ?? value.last_health_at })),
  updateProvider: (id: string, input: Partial<Provider> & { api_key?: string }) =>
    request<Provider>(`/api/v1/admin/providers/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify(input) }),
  listModels: () => request<ModelConfig[]>("/api/v1/admin/models"),
  updateModel: (id: string, input: Partial<ModelConfig> & { modes?: string[] }) =>
    request<ModelConfig>(`/api/v1/admin/models/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify(input) }),
  listRoutes: () => request<ModelRoute[]>("/api/v1/admin/routes"),
  updateRoute: (id: string, input: { weight: number; enabled: boolean; filter_rules: VideoRouteFilter }) =>
    request<ModelRoute>(`/api/v1/admin/routes/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify(input) }),
  observeRoutePrice: (id: string, rawUnitPrice: string) =>
    request<ModelRoute>(`/api/v1/admin/routes/${encodeURIComponent(id)}/price-observations`, { method: "POST", body: JSON.stringify({ raw_unit_price: rawUnitPrice }) }),
  acknowledgeRoutePrice: (id: string) => request<unknown>(`/api/v1/admin/routes/${encodeURIComponent(id)}/price-ack`, { method: "POST" }),
  listVideoPointPrices: () => request<VideoPointPrice[]>("/api/v1/admin/video-point-prices"),
  updateVideoPointPrice: (model: string, resolution: string, input: VideoPointPriceUpdate) => request<VideoPointPrice>(`/api/v1/admin/video-point-prices/${encodeURIComponent(model)}/${encodeURIComponent(resolution)}`, { method: "PUT", body: JSON.stringify(input) }),
  listAlerts: (status = "") => request<ProviderAlert[]>(`/api/v1/admin/alerts${queryString({ status })}`),
  acknowledgeAlert: (id: string) => request<unknown>(`/api/v1/admin/alerts/${encodeURIComponent(id)}/ack`, { method: "POST" }),
  resolveAlert: (id: string) => request<unknown>(`/api/v1/admin/alerts/${encodeURIComponent(id)}/resolve`, { method: "POST" }),
  listAlertWebhooks: () => request<AlertWebhook[]>("/api/v1/admin/alert-webhooks"),
  upsertAlertWebhook: (input: { name: string; url: string; signing_secret: string; event_types: string[] }) =>
    request<unknown>("/api/v1/admin/alert-webhooks", { method: "POST", body: JSON.stringify(input) }),
  listPriceVersions: async () => (await request<PriceVersion[]>("/api/v1/admin/pricing")).map((value) => ({ ...value, margin_rate: value.margin_rate ?? value.markup_rate ?? "0" })),
  createPriceVersion: (input: PricingDraftInput) =>
    request<PriceVersion>("/api/v1/admin/pricing/versions", { method: "POST", body: JSON.stringify(input) }),
	publishPriceVersion: (id: string) =>
		request<PriceVersion>(`/api/v1/admin/pricing/versions/${encodeURIComponent(id)}/publish`, { method: "POST" }),
	getAggAPIVideoPrices: () => request<AggAPIVideoPriceVersion>("/api/v1/admin/aggapi-video-prices"),
	updateAggAPIVideoPrices: (input: AggAPIVideoPriceUpdateInput) =>
		request<AggAPIVideoPriceVersion>("/api/v1/admin/aggapi-video-prices", { method: "PUT", body: JSON.stringify(input) }),
  listAdminTasks: async (params: { page?: number; status?: string; modality?: string; query?: string }) => {
    const serverParams = params;
    const page = normalizePage(await request<PageResult<Task> | Task[]>(`/api/v1/admin/tasks${queryString(serverParams)}`));
    const query = params.query?.trim().toLowerCase();
    const items = page.items.map(normalizeTask).filter((task) => (params.status !== "active" || ["preparing", "submitted", "in_progress", "storing"].includes(task.status)) && (!query || task.id.toLowerCase().includes(query) || task.model.toLowerCase().includes(query)));
    return { ...page, items, total: params.status === "active" || query ? items.length : page.total };
  },
  getAdminTask: async (id: string) => {
    const value = await request<AdminTaskDiagnostics>(`/api/v1/admin/tasks/${encodeURIComponent(id)}`);
    return {
      ...normalizeTask(value),
      events: value.events ?? [],
      provider_attempts: value.provider_attempts ?? [],
      point_transactions: value.point_transactions ?? [],
      provider_cost_entries: value.provider_cost_entries ?? []
    } as AdminTaskDiagnostics;
  },
  listAudit: async (params: { page?: number; query?: string; action?: string }) => {
    const page = normalizePage(await request<PageResult<AuditEntry> | AuditEntry[]>(`/api/v1/admin/audit${queryString(params)}`));
    const query = params.query?.trim().toLowerCase();
    const items = page.items.map((value) => ({ ...value, metadata: value.metadata ?? value.detail })).filter((entry) => !query || entry.action.toLowerCase().includes(query) || entry.resource_type.toLowerCase().includes(query) || entry.resource_id?.toLowerCase().includes(query) || entry.actor_username?.toLowerCase().includes(query) || entry.actor_user_id?.toLowerCase().includes(query));
    return { ...page, items, total: query ? items.length : page.total };
  }
};

export function taskEventURL(taskID: string) {
  return `${API_BASE}/api/v1/tasks/${encodeURIComponent(taskID)}/events`;
}

export function errorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  return "发生未知错误";
}
